import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  IonToolbar, IonTitle, IonButton, IonButtons, IonIcon, IonItem,
  IonLabel, IonInput,
  IonDatetime, IonModal, IonText, IonChip, IonSpinner,
  ModalController
} from '@ionic/angular/standalone';
import { NgClass, DatePipe } from '@angular/common';
import { format, parse, isBefore, isEqual, addDays, getDay } from 'date-fns';
import { it } from 'date-fns/locale';
import { finalize, switchMap, takeUntil } from 'rxjs';
import { NotificationService } from '../../../../services/notification.service';
import { LoaderService } from '../../../../services/loader.service';
import {
  Studente, StudentiService, EventoCalendarioDTO,
  EventoCalendarioStudenteForm, EventoCalendarioType
} from '../../../../../api-clients/generated/services';
import { CommandAction } from '../../../interfaces/command-action.interface';
import { UnsubscribeOnDestroy } from '../../../abstracts/unsubscribe-on-destroy';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChipState, getExcludedDays, isExcludedDay, getEventState, hasChiusura, getSospesoMessage
} from '../../../services/command-parser/calendar-validators';

export interface QuickChip {
  id: 'today' | 'tomorrow' | 'nextWorkday' | 'custom';
  label: string;
  date: string; // yyyy-MM-dd (empty for custom)
  shortDate: string; // e.g. "3 apr" (empty for custom)
  fullDateLabel: string;
  state: ChipState;
  stateLabel?: string;
}

@Component({
  selector: 'app-calendar-action-form',
  templateUrl: './calendar-action-form.component.html',
  styleUrls: ['./calendar-action-form.component.scss'],
  standalone: true,
  imports: [
    IonToolbar, IonTitle, IonButton, IonButtons, IonIcon, IonItem,
    IonLabel, IonInput,
    IonDatetime, IonModal, IonText, IonChip, IonSpinner,
    ReactiveFormsModule, NgClass, DatePipe
  ]
})
export class CalendarActionFormComponent extends UnsubscribeOnDestroy implements OnInit {
  @Input() action!: CommandAction;
  @Input() studente!: Studente;
  @Input() prefilledDateInizio: string | null = null;
  @Input() prefilledDateFine: string | null = null;

  @Output() saved = new EventEmitter<Record<string, never>>();
  @Output() back = new EventEmitter<void>();

  ctrlDataInizio = new FormControl<string>('');
  ctrlDataFine = new FormControl<string>('');
  todayDate = '';

  isSospeso = false;
  dateError = false;
  showDatePicker = false;
  loadingEvents = true;

  eventi: EventoCalendarioDTO[] = [];
  dayChips: QuickChip[] = [];
  customChip!: QuickChip;
  selectedChip: QuickChip | null = null;
  excludedDays: number[] = [0, 6]; // default: dom=0, sab=6

  constructor(
    private modalController: ModalController,
    private notificationService: NotificationService,
    private loaderService: LoaderService,
    private studentiService: StudentiService
  ) {
    super();
  }

  ngOnInit() {
    this.todayDate = format(new Date(), 'yyyy-MM-dd');
    this.ctrlDataInizio.setValue(this.prefilledDateInizio ?? this.todayDate);
    this.ctrlDataFine.setValue(this.prefilledDateFine ?? this.todayDate);

    // If dates were pre-filled by the parser, show the date picker section
    if (this.prefilledDateInizio) {
      this.showDatePicker = true;
    }

    this.checkSospeso();
    this.loadEventi();
  }

  get studenteFullName(): string {
    return `${this.studente?.nome ?? ''} ${this.studente?.cognome ?? ''}`.trim();
  }

  get contextQuestion(): string {
    const nome = this.studenteFullName;
    if (this.action?.actionType === 'assenza') {
      return `Quando ${nome} è assente?`;
    }
    return `Quando ${nome} mangia pasta in bianco?`;
  }

  get canSave(): boolean {
    if (this.isSospeso) return false;
    if (!this.studente?.id) return false;
    if (!this.ctrlDataInizio.value) return false;
    if (this.action?.actionType === 'assenza') {
      if (!this.ctrlDataFine.value) return false;
      if (this.dateError) return false;
    }
    return true;
  }

  get saveButtonLabel(): string {
    return this.action?.actionType === 'assenza' ? 'Salva Assenza' : 'Salva Pasta in Bianco';
  }

  get customChipLabel(): string {
    return this.action?.actionType === 'assenza' ? 'Altre date…' : 'Altro giorno…';
  }

  // --- Student ---

  private checkSospeso() {
    const message = getSospesoMessage(this.studente);
    this.isSospeso = message !== null;
    if (this.isSospeso) {
      this.notificationService.openToast({ message: message!, color: 'warning' });
    }
    this.excludedDays = getExcludedDays(this.studente);
  }

  // --- Events loading ---

  private loadEventi() {
    const studenteId = this.studente?.id;
    if (!studenteId) {
      this.eventi = [];
      this.rebuildChips();
      return;
    }
    this.loadingEvents = true;
    this.studentiService.getEventiCalendario(studenteId).pipe(
      takeUntil(this.destroy$),
      finalize(() => this.loadingEvents = false)
    ).subscribe({
      next: (eventi) => {
        this.eventi = eventi;
        this.rebuildChips();
      },
      error: () => {
        this.eventi = [];
        this.rebuildChips();
      }
    });
  }

  // --- Chips ---

  private rebuildChips() {
    const today = new Date();
    const tomorrow = addDays(today, 1);
    const nextWorkday = this.getNextWorkday(tomorrow);

    const days: QuickChip[] = [
      this.createDayChip('today', 'Oggi', today),
      this.createDayChip('tomorrow', 'Domani', tomorrow),
    ];

    if (nextWorkday.getTime() !== tomorrow.getTime()) {
      const dayNames = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];
      days.push(this.createDayChip('nextWorkday', dayNames[getDay(nextWorkday)], nextWorkday));
    }

    this.dayChips = days;
    this.customChip = {
      id: 'custom',
      label: this.customChipLabel,
      date: '',
      shortDate: '',
      fullDateLabel: '',
      state: 'libero',
    };

    // If selected chip is now occupied, deselect
    if (this.selectedChip && this.selectedChip.id !== 'custom') {
      const updated = days.find(c => c.id === this.selectedChip!.id);
      if (updated && updated.state !== 'libero') {
        this.selectedChip = null;
      }
    }
  }

  private createDayChip(id: 'today' | 'tomorrow' | 'nextWorkday', label: string, date: Date): QuickChip {
    const dateStr = format(date, 'yyyy-MM-dd');
    const dateFmt = format(date, 'dd/MM/yyyy');
    const shortDate = format(date, 'd MMM', { locale: it });
    const fullDateLabel = format(date, 'd MMMM yyyy', { locale: it });

    const excluded = isExcludedDay(date, this.excludedDays);
    const state: ChipState = excluded ? 'chiusura' : getEventState(dateFmt, this.eventi);
    const stateLabel = excluded ? 'Chiusura' : this.getStateLabel(state);

    return { id, label, date: dateStr, shortDate, fullDateLabel, state, stateLabel };
  }

  private getStateLabel(state: ChipState): string | undefined {
    switch (state) {
      case 'assenza': return 'Assenza';
      case 'pasta-in-bianco': return 'Pasta bianco';
      case 'chiusura': return 'Chiusura';
      default: return undefined;
    }
  }

  selectChip(chip: QuickChip) {
    if (chip.id !== 'custom' && chip.state !== 'libero') return;

    this.selectedChip = chip;

    if (chip.id === 'custom') {
      this.showDatePicker = true;
    } else {
      this.showDatePicker = false;
      this.ctrlDataInizio.setValue(chip.date);
      if (this.action?.actionType === 'assenza') {
        this.ctrlDataFine.setValue(chip.date);
      }
      this.validateDates();
    }
  }

  isChipDisabled(chip: QuickChip): boolean {
    return chip.id !== 'custom' && chip.state !== 'libero';
  }

  getChipCssClass(chip: QuickChip): Record<string, boolean> {
    return {
      'chip-selected': chip === this.selectedChip,
      'chip-assenza': chip.state === 'assenza',
      'chip-pasta': chip.state === 'pasta-in-bianco',
      'chip-chiusura': chip.state === 'chiusura',
    };
  }

  getChipAriaLabel(chip: QuickChip): string {
    if (chip.id === 'custom') return 'Scegli date personalizzate';
    const actionLabel = this.action?.actionType === 'assenza' ? 'assenza' : 'pasta in bianco';
    if (chip.state !== 'libero') {
      return `${chip.label}, ${chip.fullDateLabel} - ${chip.stateLabel} già presente`;
    }
    return `Imposta ${actionLabel} per ${chip.label.toLowerCase()}, ${chip.fullDateLabel}`;
  }

  private getNextWorkday(fromDate: Date): Date {
    let date = fromDate;
    while (isExcludedDay(date, this.excludedDays)) {
      date = addDays(date, 1);
    }
    return date;
  }

  // --- Date validation ---

  onDateChange() {
    this.selectedChip = this.customChip;
    this.validateDates();
  }

  private validateDates() {
    if (this.action?.actionType !== 'assenza') {
      this.dateError = false;
      return;
    }
    const inizio = this.ctrlDataInizio.value;
    const fine = this.ctrlDataFine.value;
    if (!inizio || !fine) {
      this.dateError = false;
      return;
    }
    const dInizio = parse(inizio, 'yyyy-MM-dd', new Date());
    const dFine = parse(fine, 'yyyy-MM-dd', new Date());
    this.dateError = !isBefore(dInizio, dFine) && !isEqual(dInizio, dFine);
  }

  // --- Save ---

  save() {
    if (!this.canSave) return;

    const studenteId = this.studente.id!;
    const dataInizioIso = this.ctrlDataInizio.value!;
    const dataInizioFmt = format(parse(dataInizioIso, 'yyyy-MM-dd', new Date()), 'dd/MM/yyyy');

    if (hasChiusura(dataInizioFmt, this.eventi)) {
      this.notificationService.openToast({
        message: 'Giorno di chiusura scolastica, impossibile inserire eventi',
        color: 'warning'
      });
      return;
    }

    const dataFineFmt = this.action.actionType === 'assenza'
      ? format(parse(this.ctrlDataFine.value!, 'yyyy-MM-dd', new Date()), 'dd/MM/yyyy')
      : dataInizioFmt;

    if (this.action.actionType === 'assenza' && dataFineFmt !== dataInizioFmt && hasChiusura(dataFineFmt, this.eventi)) {
      this.notificationService.openToast({
        message: 'La data di fine cade in un giorno di chiusura scolastica',
        color: 'warning'
      });
      return;
    }

    const tipoEvento = this.action.actionType === 'assenza'
      ? EventoCalendarioType.ASSENZA
      : EventoCalendarioType.PASTAINBIANCO;

    const body: EventoCalendarioStudenteForm = {
      data_inizio: dataInizioFmt,
      data_fine: dataFineFmt,
      tipo_evento: tipoEvento,
    };

    this.loaderService.showLoaderObserve$().pipe(
      switchMap(() => this.studentiService.addEventoCalendario(body, studenteId)),
      takeUntil(this.destroy$),
      finalize(() => this.loaderService.hideLoader())
    ).subscribe({
      next: () => {
        this.loaderService.hideLoader();
        const label = this.action.actionType === 'assenza' ? 'Assenza salvata correttamente' : 'Pasta in bianco salvata correttamente';
        this.notificationService.openToast({ message: label, color: 'success' });
        this.saved.emit({});
      },
      error: (error: HttpErrorResponse) => {
        this.notificationService.openToast({
          message: error.error?.message || 'Errore durante il salvataggio',
          color: 'danger'
        });
      }
    });
  }

  goBack() {
    this.back.emit();
  }

  closeDateModal() {
    this.modalController.dismiss();
  }
}
